import { format } from 'date-fns';

/**
 * Format a date to a human-readable string
 */
export function formatDate(date: Date | string | undefined | null, formatStr = 'MMM d, yyyy'): string {
  if (!date) {
    return 'N/A';
  }

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    return format(dateObj, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Format a duration in seconds to MM:SS format
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * A duration in the words a coach reads, not a number they have to convert.
 *
 * "2 minutes" / "1 minute" / "90 seconds" — used wherever a cap or a limit is
 * stated in coach-facing copy (docs/COACH_FACING_COPY_STANDARD.md §2: numbers
 * only when the reader can act on them).
 */
export function describeDurationInWords(seconds: number): string {
  if (seconds >= 60 && seconds % 60 === 0) {
    const minutes = seconds / 60;
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }
  return `${Math.round(seconds)} seconds`;
}

/**
 * Format a score as a percentage
 * Handles both 0-1 range and 0-100 range scores
 */
export function formatScore(score: number, decimals = 0): string {
  // If score is already in 0-100 range (likely from backend), use it directly
  // Otherwise assume it's 0-1 range and multiply by 100
  const percentage = score > 1 ? score : score * 100;
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Return a plain-language label for a 0-100 score.
 * Returns "—" for NaN, null, or undefined inputs.
 *
 * F12 reconciliation (2026-09-21 report redesign — decided in-build, per
 * backlog/eval-report-redesign-artifact-inspired.md): this global 90/75/60
 * threshold bucket is a non-anchored display idiom, scoped to OVERALL/
 * dashboard-level rollups (Athlete/CoachDashboard). It is deliberately kept
 * as-is here, NOT retired, because it does not claim per-checkpoint
 * calibration — that claim only matters at the goal/checkpoint level, where
 * decision 1 requires calibration-gated wording (see
 * GoalResultsSection.tsx's CalibrationHonestNote / "not yet calibrated").
 * Do not reuse this bucket for checkpoint-level words — it would
 * "backfill from the global 80/60", which decision 1 explicitly forbids.
 */
export function scoreLabel(score: number | null | undefined): string {
  if (score == null || isNaN(score as number)) return '—';
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 60) return 'Getting There';
  return 'Needs Work';
}

/**
 * Format a file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

